import React, { memo, useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  Background,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
} from '@xyflow/react'
import {
  CheckCircle2,
  GitBranch,
  Globe2,
  Hourglass,
  SlidersHorizontal,
  Workflow,
  X,
  Zap,
} from 'lucide-react'
import '@xyflow/react/dist/style.css'
import './N8nWorkflowFlowModal.css'
import { useTranslation } from 'react-i18next'
import { getAdminN8nWorkflow } from '../../api/adminN8nWorkflows'
import { getAiDependencyEdgePath, getWorkflowEdgePath } from './N8nWorkflowEdgePath'

const NODE_THEME = {
  trigger: {
    container: 'border-emerald-500 bg-emerald-400 text-emerald-950 shadow-emerald-100/80',
    iconBox: 'bg-white/35 text-emerald-900',
    detail: 'text-emerald-800/90',
    handle: '!bg-emerald-700',
    icon: Zap,
  },
  request: {
    container: 'border-sky-200 bg-white text-slate-800 shadow-sky-100/80',
    iconBox: 'bg-sky-50 text-sky-600',
    detail: 'text-sky-600',
    handle: '!bg-sky-600',
    icon: Globe2,
  },
  decision: {
    container: 'border-violet-200 bg-white text-slate-800 shadow-violet-100/80',
    iconBox: 'bg-violet-50 text-violet-600',
    detail: 'text-violet-600',
    handle: '!bg-violet-600',
    icon: GitBranch,
  },
  wait: {
    container: 'border-amber-200 bg-white text-slate-800 shadow-amber-100/80',
    iconBox: 'bg-amber-50 text-amber-600',
    detail: 'text-amber-600',
    handle: '!bg-amber-600',
    icon: Hourglass,
  },
  transform: {
    container: 'border-indigo-200 bg-white text-slate-800 shadow-indigo-100/80',
    iconBox: 'bg-indigo-50 text-indigo-600',
    detail: 'text-indigo-600',
    handle: '!bg-indigo-600',
    icon: SlidersHorizontal,
  },
  end: {
    container: 'border-emerald-300 bg-emerald-50 text-emerald-950 shadow-emerald-100/80',
    iconBox: 'bg-emerald-100 text-emerald-700',
    detail: 'text-emerald-700',
    handle: '!bg-emerald-600',
    icon: CheckCircle2,
  },
  action: {
    container: 'border-slate-200 bg-white text-slate-800 shadow-slate-100',
    iconBox: 'bg-slate-100 text-slate-600',
    detail: 'text-slate-500',
    handle: '!bg-slate-600',
    icon: Workflow,
  },
}

const NODE_WIDTH = 180
const HORIZONTAL_GAP = 104
const VERTICAL_GAP = 154
const MIN_HORIZONTAL_NODE_SPACING = NODE_WIDTH + 24
const AI_TARGET_HANDLES = [
  { label: 'ai_languageModel', id: 'ai-language-model', left: '28%' },
  { label: 'ai_memory', id: 'ai-memory', left: '50%' },
  { label: 'ai_tool', id: 'ai-tool', left: '72%' },
]
const AI_TARGET_HANDLE_BY_LABEL = new Map(
  AI_TARGET_HANDLES.map(({ label, id }) => [label, id])
)

function isCanceled(error) {
  return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError'
}

function workflowIdOf(workflow) {
  const id = workflow?.id ?? workflow?.workflow_id
  if (id === null || id === undefined || id === '') return null
  return String(id)
}

function isStickyNode(node) {
  return String(node?.type || '').toLowerCase().includes('stickynote')
}

function nodeKind(node, isEnd = false) {
  const type = String(node?.type || '').toLowerCase()
  if (type.includes('trigger')) return 'trigger'
  if (isEnd) return 'end'
  if (type.includes('httprequest') || type.includes('webhook')) return 'request'
  if (type.includes('.if') || type.includes('switch') || type.includes('filter')) return 'decision'
  if (type.includes('wait') || type.includes('delay') || type.includes('schedule')) return 'wait'
  if (type.includes('.set') || type.includes('editfields') || type.includes('code')) return 'transform'
  return 'action'
}

function shortNodeType(type) {
  const value = String(type || '')
  return value.split('.').pop() || ''
}

const WorkflowNode = memo(function WorkflowNode({ data }) {
  const theme = NODE_THEME[data.kind] || NODE_THEME.action
  const Icon = theme.icon

  return (
    <div
      className={`workflow-node-card relative w-[180px] rounded-xl border px-3 py-2.5 shadow-sm ${theme.container}`}
      data-node-kind={data.kind}
    >
      {!data.isRoot && (
        <Handle
          id="main-target"
          type="target"
          position={Position.Left}
          className={`!h-2 !w-2 !border-2 !border-white ${theme.handle}`}
        />
      )}
      <div className="flex items-start gap-3 text-left">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${theme.iconBox}`}>
          <Icon aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="break-words text-xs font-semibold leading-snug">{data.name}</div>
          {data.nodeType && (
            <div className={`mt-1 break-all text-[10px] leading-snug ${theme.detail}`}>{data.nodeType}</div>
          )}
        </div>
        <span className="shrink-0 text-[10px] font-medium opacity-45">{data.stepNumber}</span>
      </div>
      {!data.isEnd && (
        <Handle
          id="main-source"
          type="source"
          position={Position.Right}
          className={`!h-2 !w-2 !border-2 !border-white ${theme.handle}`}
        />
      )}
      {data.hasAiSource && (
        <Handle
          id="ai-source"
          type="source"
          position={Position.Top}
          className="!h-2 !w-2 !border-2 !border-white !bg-slate-400"
        />
      )}
      {data.aiTargetHandles.map((handle) => (
        <Handle
          key={handle.id}
          id={handle.id}
          type="target"
          position={Position.Bottom}
          style={{ left: handle.left }}
          className="!h-2 !w-2 !border-2 !border-white !bg-slate-400"
        />
      ))}
    </div>
  )
})

const NODE_TYPES = { workflow: WorkflowNode }

function AnimatedWorkflowEdge({
  label,
  sourceX,
  sourceY,
  sourcePosition,
  targetX,
  targetY,
  targetPosition,
}) {
  const [edgePath, labelX, labelY] = getWorkflowEdgePath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  return (
    <>
      <path d={edgePath} className="workflow-edge-track" />
      <path d={edgePath} className="workflow-edge-pulse" />
      {label && (
        <EdgeLabelRenderer>
          <span
            className="pointer-events-none absolute rounded-full border border-blue-100 bg-white/95 px-2 py-0.5 text-[9px] font-medium text-blue-500 shadow-sm"
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
          >
            {label}
          </span>
        </EdgeLabelRenderer>
      )}
    </>
  )
}

function AiDependencyEdge({
  sourceX,
  sourceY,
  sourcePosition,
  targetX,
  targetY,
  targetPosition,
}) {
  const [edgePath] = getAiDependencyEdgePath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  return <path d={edgePath} className="workflow-ai-edge" />
}

const EDGE_TYPES = {
  aiDependency: AiDependencyEdge,
  animatedWorkflow: AnimatedWorkflowEdge,
}

function createTopDownLayout(nodes, connections) {
  const nodeById = new Map(nodes.map((node) => [String(node.id), node]))
  const indegree = new Map(nodes.map((node) => [String(node.id), 0]))
  const outgoing = new Map(nodes.map((node) => [String(node.id), []]))

  connections.forEach((connection) => {
    const source = String(connection.source)
    const target = String(connection.target)
    if (!nodeById.has(source) || !nodeById.has(target)) return
    outgoing.get(source).push(target)
    indegree.set(target, indegree.get(target) + 1)
  })

  const roots = nodes
    .map((node) => String(node.id))
    .filter((id) => indegree.get(id) === 0)
  const queue = [...roots]
  const depth = new Map(roots.map((id) => [id, 0]))
  const visited = new Set()

  while (queue.length > 0) {
    const id = queue.shift()
    if (visited.has(id)) continue
    visited.add(id)
    outgoing.get(id).forEach((target) => {
      depth.set(target, Math.max(depth.get(target) || 0, (depth.get(id) || 0) + 1))
      indegree.set(target, indegree.get(target) - 1)
      if (indegree.get(target) === 0) queue.push(target)
    })
  }

  let fallbackDepth = Math.max(0, ...depth.values())
  nodes.forEach((node) => {
    const id = String(node.id)
    if (!depth.has(id)) {
      fallbackDepth += 1
      depth.set(id, fallbackDepth)
    }
  })

  const layers = new Map()
  nodes.forEach((node) => {
    const layer = depth.get(String(node.id)) || 0
    if (!layers.has(layer)) layers.set(layer, [])
    layers.get(layer).push(node)
  })

  const positions = new Map()
  layers.forEach((layerNodes, layer) => {
    layerNodes.sort((left, right) => (left?.position?.x || 0) - (right?.position?.x || 0))
    const layerWidth = (layerNodes.length - 1) * (NODE_WIDTH + HORIZONTAL_GAP)
    layerNodes.forEach((node, index) => {
      positions.set(String(node.id), {
        x: index * (NODE_WIDTH + HORIZONTAL_GAP) - layerWidth / 2,
        y: layer * VERTICAL_GAP,
      })
    })
  })

  return { positions, outgoing, roots: new Set(roots) }
}

function n8nPosition(node) {
  const position = node?.position
  const x = Array.isArray(position) ? position[0] : position?.x
  const y = Array.isArray(position) ? position[1] : position?.y
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null
  return { x, y }
}

function createDisplayPositions(nodes, fallbackPositions) {
  const positions = new Map(nodes.map((node) => {
    const id = String(node.id)
    return [id, n8nPosition(node) ?? fallbackPositions.get(id)]
  }))
  const rows = new Map()

  nodes.forEach((node) => {
    const id = String(node.id)
    const position = positions.get(id)
    if (!position) return
    if (!rows.has(position.y)) rows.set(position.y, [])
    rows.get(position.y).push({ id, ...position })
  })

  rows.forEach((row) => {
    if (row.length < 2) return
    row.sort((left, right) => left.x - right.x)
    const adjustedX = [row[0].x]
    for (let index = 1; index < row.length; index += 1) {
      adjustedX.push(Math.max(
        row[index].x,
        adjustedX[index - 1] + MIN_HORIZONTAL_NODE_SPACING
      ))
    }
    if (adjustedX.every((x, index) => x === row[index].x)) return

    const originalCenter = (row[0].x + row.at(-1).x) / 2
    const adjustedCenter = (adjustedX[0] + adjustedX.at(-1)) / 2
    const centerOffset = originalCenter - adjustedCenter
    row.forEach((node, index) => {
      positions.set(node.id, { x: adjustedX[index] + centerOffset, y: node.y })
    })
  })

  return positions
}

function toFlow(detail) {
  const safeNodes = Array.isArray(detail?.nodes) ? detail.nodes : []
  const safeConnections = Array.isArray(detail?.connections)
    ? detail.connections
    : (Array.isArray(detail?.edges) ? detail.edges : [])

  const executableNodes = safeNodes.filter(
    (node) => node?.id !== null
      && node?.id !== undefined
      && node?.id !== ''
      && !isStickyNode(node)
  )
  const nodeIds = new Set(executableNodes.map((node) => String(node.id)))
  const validConnections = safeConnections
    .filter((connection) => {
      const source = String(connection?.source ?? '')
      const target = String(connection?.target ?? '')
      return nodeIds.has(source) && nodeIds.has(target)
    })
  const mainConnections = validConnections.filter(
    (connection) => !AI_TARGET_HANDLE_BY_LABEL.has(connection?.label)
  )
  const aiSourceIds = new Set()
  const aiTargetLabels = new Map()
  validConnections.forEach((connection) => {
    if (!AI_TARGET_HANDLE_BY_LABEL.has(connection?.label)) return
    const source = String(connection.source)
    const target = String(connection.target)
    aiSourceIds.add(source)
    if (!aiTargetLabels.has(target)) aiTargetLabels.set(target, new Set())
    aiTargetLabels.get(target).add(connection.label)
  })
  const layout = createTopDownLayout(executableNodes, mainConnections)
  const displayPositions = createDisplayPositions(executableNodes, layout.positions)
  const nodes = executableNodes.map((node, index) => {
    const id = String(node.id)
    const isEnd = layout.outgoing.get(id).length === 0

    return {
      id,
      type: 'workflow',
      position: displayPositions.get(id),
      data: {
        kind: nodeKind(node, isEnd),
        name: node?.name || id,
        nodeType: shortNodeType(node?.type),
        stepNumber: String(index + 1).padStart(2, '0'),
        isRoot: layout.roots.has(id),
        isEnd,
        hasAiSource: aiSourceIds.has(id),
        aiTargetHandles: AI_TARGET_HANDLES
          .filter(({ label }) => aiTargetLabels.get(id)?.has(label))
          .map(({ id: handleId, left }) => ({ id: handleId, left })),
      },
    }
  })
  const edges = validConnections
    .map((connection, index) => {
      const aiTargetHandle = AI_TARGET_HANDLE_BY_LABEL.get(connection?.label)
      if (aiTargetHandle) {
        return {
          id: `connection-${index}-${connection.source}-${connection.target}`,
          source: String(connection.source),
          target: String(connection.target),
          sourceHandle: 'ai-source',
          targetHandle: aiTargetHandle,
          label: undefined,
          type: 'aiDependency',
          animated: false,
        }
      }
      const label = typeof connection?.label === 'string' && connection.label !== 'main'
        ? connection.label
        : undefined

      return {
        id: `connection-${index}-${connection.source}-${connection.target}`,
        source: String(connection.source),
        target: String(connection.target),
        sourceHandle: 'main-source',
        targetHandle: 'main-target',
        label,
        type: 'animatedWorkflow',
        animated: true,
        labelStyle: { fontSize: 10, fill: '#64748b', fontWeight: 500 },
        labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
      }
    })

  return { nodes, edges }
}

function workflowLabel(workflow) {
  if (workflow?.industry) return String(workflow.industry)
  if (!Array.isArray(workflow?.tags) || workflow.tags.length === 0) return ''
  const firstTag = workflow.tags[0]
  return typeof firstTag === 'string' ? firstTag : (firstTag?.name || '')
}

function WorkflowMetadata({ detail, workflow, t }) {
  const label = workflowLabel(workflow)
  const triggerNode = Array.isArray(detail?.nodes)
    ? detail.nodes.find((node) => nodeKind(node) === 'trigger')
    : null
  const trigger = workflow?.trigger_event || triggerNode?.name
  const hasVersion = workflow?.version !== null
    && workflow?.version !== undefined
    && workflow?.version !== ''
  const statusLabel = detail?.n8n_active === true
    ? t('automations.admin.workflows.n8nActive')
    : t('automations.admin.workflows.n8nInactive')
  const items = [label, trigger, hasVersion ? `v${workflow.version}` : statusLabel].filter(Boolean)

  return (
    <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-2 text-xs text-slate-400">
      {items.map((item, index) => (
        <React.Fragment key={`${item}-${index}`}>
          {index > 0 && <span aria-hidden="true" className="text-slate-300">·</span>}
          <span className="flex items-center gap-1 truncate">
            {item === trigger && <Zap aria-hidden="true" className="h-3 w-3 text-orange-500" />}
            {item}
          </span>
        </React.Fragment>
      ))}
    </div>
  )
}

export default function N8nWorkflowFlowModal({ open, onClose, workflow }) {
  const { t } = useTranslation()
  const titleId = useId()
  const workflowId = workflowIdOf(workflow)
  const [detail, setDetail] = useState(null)
  const [status, setStatus] = useState('idle')
  const [resolvedWorkflowId, setResolvedWorkflowId] = useState(null)
  const [retryKey, setRetryKey] = useState(0)
  const requestSequenceRef = useRef(0)

  useEffect(() => {
    if (!open || !workflowId) return undefined
    const controller = new AbortController()
    const requestSequence = ++requestSequenceRef.current

    getAdminN8nWorkflow(workflowId, { signal: controller.signal })
      .then((data) => {
        if (requestSequence !== requestSequenceRef.current) return
        setDetail(data?.workflow ?? data)
        setResolvedWorkflowId(workflowId)
        setStatus('success')
      })
      .catch((error) => {
        if (isCanceled(error) || requestSequence !== requestSequenceRef.current) return
        setDetail(null)
        setResolvedWorkflowId(workflowId)
        setStatus('error')
      })

    return () => controller.abort()
  }, [open, retryKey, workflowId])

  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, open])

  const flow = useMemo(() => toFlow(detail), [detail])
  const currentStatus = workflowId && resolvedWorkflowId !== workflowId ? 'loading' : status
  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-[2px] sm:p-6"
      onClick={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex h-[88vh] min-h-[520px] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-slate-100 px-7 py-5">
          <div className="min-w-0">
            <h2 id={titleId} className="truncate text-lg font-semibold text-slate-800">
              {detail?.name || workflow?.name}
            </h2>
            {detail && <WorkflowMetadata detail={detail} workflow={workflow} t={t} />}
          </div>
          <button
            type="button"
            aria-label={t('automations.admin.workflows.detailCloseLabel')}
            onClick={onClose}
            className="ml-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </header>

        <div className="relative flex-1 overflow-hidden bg-slate-50/30">
          {currentStatus === 'loading' && (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              {t('automations.admin.workflows.detailLoading')}
            </div>
          )}
          {currentStatus === 'error' && (
            <div className="flex h-full flex-col items-center justify-center text-sm text-red-500">
              <p>{t('automations.admin.workflows.detailError')}</p>
              <button
                type="button"
                onClick={() => {
                  setStatus('loading')
                  setRetryKey((value) => value + 1)
                }}
                className="mt-4 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                {t('automations.admin.retry')}
              </button>
            </div>
          )}
          {currentStatus === 'success' && flow.nodes.length === 0 && (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              {t('automations.admin.workflows.detailNoNodes')}
            </div>
          )}
          {currentStatus === 'success' && flow.nodes.length > 0 && (
            <ReactFlow
              nodes={flow.nodes}
              edges={flow.edges}
              nodeTypes={NODE_TYPES}
              edgeTypes={EDGE_TYPES}
              nodesDraggable={false}
              nodesConnectable={false}
              nodesFocusable={false}
              edgesFocusable={false}
              elementsSelectable={false}
              fitView
              fitViewOptions={{ padding: 0.25, minZoom: 0.45, maxZoom: 1.1 }}
              minZoom={0.25}
              maxZoom={1.5}
              proOptions={{ hideAttribution: false }}
              className="n8n-workflow-flow [&_.react-flow__controls]:overflow-hidden [&_.react-flow__controls]:rounded-md [&_.react-flow__controls]:border [&_.react-flow__controls]:border-slate-200 [&_.react-flow__controls]:shadow-sm [&_.react-flow__minimap]:border-8 [&_.react-flow__minimap]:border-white [&_.react-flow__minimap]:shadow-md"
            >
              <Background color="#dbe3ef" gap={18} size={1} />
              <Controls showInteractive={false} position="bottom-left" />
              <MiniMap
                position="bottom-right"
                pannable
                zoomable
                maskColor="rgba(248, 250, 252, 0.72)"
                nodeColor={(node) => {
                  if (node.data?.kind === 'trigger') return '#34d399'
                  if (node.data?.kind === 'end') return '#a7f3d0'
                  if (node.data?.kind === 'decision') return '#ddd6fe'
                  if (node.data?.kind === 'wait') return '#fde68a'
                  return '#dbeafe'
                }}
              />
            </ReactFlow>
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-slate-100 px-7 py-3.5">
          <span className="text-xs text-slate-400">{t('automations.admin.workflows.detailReadOnly')}</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-slate-100 px-5 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            {t('automations.admin.workflows.detailClose')}
          </button>
        </footer>
      </section>
    </div>
  )
}
